var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from "react";
import { useCombobox, useMultipleSelection } from "downshift";
import { classnames } from "../../../utils";
import { useOutsideClick } from "../../../internal-hooks/useOutsideClick";
import { useOutsideFocus } from "../../../internal-hooks/useOutsideFocus";
import { useMutationObserver } from "../../../internal-hooks/useMutationObserver";
import { ChevronDownIcon } from "../../Icons/ChevronDownIcon";
import { CloseIcon } from "../../Icons/CloseIcon";
import { Checkbox } from "../../Checkbox";
import { Text } from "../../Text";
import { MultiSelectFieldStyle, MultiSelectPopper } from "./style";
import { MultiSelectTag } from "./MultiSelectTag";
import { MultiSelectGroupTag } from "./MultiSelectGroupTag";
var MultiSelectFieldAsync = function (_a) {
    var id = _a.id, className = _a.className, style = _a.style, label = _a.label, _b = _a.selectedGroupTag, selectedGroupTag = _b === void 0 ? false : _b, _c = _a.selectAll, selectAll = _c === void 0 ? false : _c, _d = _a.disabled, disabled = _d === void 0 ? false : _d, itemsToDisplay = _a.itemsToDisplay, footerMessage = _a.footerMessage, selectedItems = _a.value, onChange = _a.onChange, onRequestNewItem = _a.onRequestNewItem, onRequestItems = _a.onRequestItems, onScroll = _a.onScroll;
    // In the future we may need to also allow a forwardRef here,
    //  in which case we should spy on this ref and propagate it to the forwardRef with a useEffect hook.
    var widgetRef = React.useRef(null);
    var inputRef = React.useRef(null);
    var menuRef = React.useRef(null);
    var _e = __read(React.useState(""), 2), inputValue = _e[0], setInputValue = _e[1];
    var _f = __read(React.useState([]), 2), items = _f[0], setItems = _f[1];
    var _g = __read(React.useState(false), 2), isOpen = _g[0], setIsOpen = _g[1];
    var _h = __read(React.useState(0), 2), selfWidth = _h[0], setSelfWidth = _h[1];
    var allItemsSelected = selectedItems.length === items.length;
    React.useEffect(function () {
        onRequestItems(inputValue)
            .then(function (items) {
            if (!disabled) {
                setItems(items);
            }
        })
            .catch(function (e) {
            console.error(e);
        });
    }, [inputValue, onRequestItems]);
    var onClick = React.useCallback(function (e) {
        var _a;
        e.preventDefault();
        setIsOpen(true);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var onFocus = React.useCallback(function () {
        setIsOpen(true);
    }, []);
    var onClose = React.useCallback(function () {
        setIsOpen(false);
    }, []);
    var onItemChanged = React.useCallback(function (selected, includeItem) {
        var itemIndex = selectedItems.findIndex(function (item) { return item.value === selected.value; });
        var hasItem = itemIndex !== -1;
        if (hasItem === includeItem) {
            return;
        }
        if (includeItem) {
            onChange(__spreadArray(__spreadArray([], __read(selectedItems), false), [selected], false));
        }
        else {
            if (itemIndex !== -1) {
                onChange(__spreadArray(__spreadArray([], __read(selectedItems.slice(0, itemIndex)), false), __read(selectedItems.slice(itemIndex + 1)), false));
            }
        }
    }, [onChange, selectedItems]);
    var onItemRemoved = React.useCallback(function (value) {
        onItemChanged({ value: value }, false);
    }, [onItemChanged]);
    var onRemoveAll = React.useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        onChange([]);
    }, [onChange]);
    var onClear = React.useCallback(function () {
        onChange([]);
    }, [onChange]);
    var onChangeSelectAll = React.useCallback(function () {
        if (allItemsSelected) {
            onChange([]);
        }
        else {
            onChange(items);
        }
    }, [onChange, items, allItemsSelected]);
    // Close the menu if the user goes somewhere else.
    useOutsideClick([widgetRef, menuRef], onClose, isOpen);
    useOutsideFocus([widgetRef, menuRef], onClose, isOpen);
    // Observe for size changes and update our menu width to match.
    // useLayoutEffect handles the initial sizing, and useMutationObserver
    // handles size updates due to child changes.
    React.useLayoutEffect(function () {
        var _a, _b, _c;
        setSelfWidth((_c = (_b = (_a = widgetRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 0);
    }, [widgetRef.current]);
    useMutationObserver(widgetRef.current, function () {
        var _a, _b, _c;
        setSelfWidth((_c = (_b = (_a = widgetRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 0);
    });
    // downshift implements the bulk of our keyboard interaction and aria compliance.
    var onInputKeyDown = React.useCallback(function (e) {
        if (e.keyCode === 8 && inputValue.length === 0) {
            // Backspace pressed when nothing is typed
            // Delete the last item
            if (selectedGroupTag) {
                onChange([]);
            }
            else {
                onChange(selectedItems.slice(0, -1));
            }
        }
    }, [onChange, selectedItems, inputValue]);
    var _j = useMultipleSelection({
        // We are a controlled component, so we declare the selected items
        // directly.
        selectedItems: selectedItems
    }), getSelectedItemProps = _j.getSelectedItemProps, getDropdownProps = _j.getDropdownProps;
    var _k = useCombobox({
        // This is used as a prefix to generate multiple IDs, so it is safe to pass directly
        //  while also
        id: id,
        isOpen: isOpen,
        inputValue: inputValue,
        items: getItemsToDisplay(items, itemsToDisplay),
        itemToString: itemToString,
        stateReducer: function (state, _a) {
            var type = _a.type, changes = _a.changes;
            switch (type) {
                case useCombobox.stateChangeTypes.InputChange:
                    changes = __assign(__assign({}, changes), { highlightedIndex: 0 });
                    break;
                // Maintain the highlighted item when selecting values or on mouse leave.
                //  This lets us continue using the keyboard to navigate to further items.
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                case useCombobox.stateChangeTypes.MenuMouseLeave:
                    changes = __assign(__assign({}, changes), { highlightedIndex: state.highlightedIndex });
                    break;
            }
            return changes;
        },
        onStateChange: function (_a) {
            // selectedItem is the combobox's idea of its current selection,
            //  which usually follows highlightedIndex.  We enforce this
            //  in the reducer, to communicate the item that should be selected.
            //  This particularly applies to hitting enter in the combobox.
            var newInputValue = _a.inputValue, type = _a.type, selectedItem = _a.selectedItem;
            // We need to call selectItem(null) to deselect the item from useCombobox
            //  to allow us to select another one.  We have to cast null as any
            //  because the typings for useCombobox incorrectly state the item is
            //  not nullable.
            switch (type) {
                case useCombobox.stateChangeTypes.InputChange:
                    setInputValue(newInputValue !== null && newInputValue !== void 0 ? newInputValue : "");
                    break;
                case useCombobox.stateChangeTypes.ItemClick:
                    // On click, toggle the clicked item.
                    // We want to use the selectedItem state from the exterior hook, not
                    // the selectedItem from the change object for onStateChange.
                    // This is because the onStateChange selectedItem will be undefined if the item has not changed,
                    //  as is the case if we click the same item multiple times.
                    if (selectedItem) {
                        onItemChanged(selectedItem, selectedItems.findIndex(function (item) { return item.value === selectedItem.value; }) === -1);
                        selectItem(null);
                    }
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownEnter: {
                    // On enter with an item chosen, select it in the multiselect
                    if (selectedItem) {
                        setInputValue("");
                        onItemChanged(selectedItem, selectedItems.findIndex(function (item) { return item.value === selectedItem.value; }) === -1);
                        selectItem(null);
                    }
                    // Note: This inputValue is the useState input value, not the new input value for the state change.
                    else if (onRequestNewItem && inputValue.length) {
                        setInputValue("");
                        onRequestNewItem(inputValue);
                        selectItem(null);
                    }
                    break;
                }
            }
        }
    }), getToggleButtonProps = _k.getToggleButtonProps, getLabelProps = _k.getLabelProps, getMenuProps = _k.getMenuProps, getInputProps = _k.getInputProps, getComboboxProps = _k.getComboboxProps, highlightedIndex = _k.highlightedIndex, getItemProps = _k.getItemProps, selectItem = _k.selectItem;
    return (React.createElement(MultiSelectFieldStyle, { className: "bn-multiselect-container" },
        React.createElement(Text, __assign({}, getLabelProps(), { as: "label", variant: "label" }), label),
        React.createElement("div", { ref: widgetRef, id: id, className: classnames(className, disabled
                ? "bn-multiselect-widget--disabled"
                : "bn-multiselect-widget", isOpen && "bn-multiselect-widget--focused"), style: style, onClick: onClick },
            selectedGroupTag
                ? selectedItems.length > 0 && (React.createElement(MultiSelectGroupTag, { selectedItems: selectedItems, onRequestRemove: onClear }))
                : selectedItems.map(function (item, index) { return (React.createElement(MultiSelectTag, __assign({ key: keyIndex(item, index), label: item.label, value: item.value }, getSelectedItemProps({ selectedItem: item, index: index }), { onRequestRemove: onItemRemoved }))); }),
            React.createElement("div", __assign({ className: "bn-multiselect-combobox" }, getComboboxProps()),
                React.createElement("input", __assign({}, getInputProps(__assign(__assign({}, getDropdownProps({
                    preventKeyAction: isOpen,
                    ref: inputRef,
                    "aria-multiselectable": true,
                    "aria-autocomplete": "list"
                })), { onKeyDown: onInputKeyDown })), { className: "bn-multiselect-input", onFocus: onFocus, placeholder: disabled ? "——" : "Search...", disabled: disabled })),
                selectedItems.length > 0 ? (React.createElement(CloseIcon, { size: "small", className: "bn-multiselect-clear", onClick: onRemoveAll })) : (React.createElement(ChevronDownIcon, __assign({}, getToggleButtonProps(), { size: "small", className: "bn-multiselect-droparrow" }))))),
        React.createElement(MultiSelectPopper, { isOpen: isOpen, anchorEl: widgetRef.current, placement: "bottom-start", "$minWidth": Math.max(selfWidth, 100) },
            React.createElement("div", __assign({}, getMenuProps({ ref: menuRef }, {
                // Not sure why we have to suppress this, but the ref is properly being set.
                //  We know because our menuRef is working fine.
                suppressRefError: true
            }), { className: "bn-multiselect-menu", onScroll: onScroll }),
                selectAll && items.length > 0 && (React.createElement("div", { className: classnames("bn-multiselect-item", "bn-multiselect-item-select-all") },
                    React.createElement(Checkbox, { checked: allItemsSelected, onChange: onChangeSelectAll }, "Select All"))),
                getItemsToDisplay(items, itemsToDisplay).map(function (item, index) {
                    var isSelected = selectedItems.findIndex(function (currentItem) { return currentItem.value === item.value; }) !== -1;
                    return (React.createElement("div", __assign({ key: keyIndex(item, index) }, getItemProps({ item: item, index: index, onClick: consumeMouseEvent }), { className: classnames("bn-multiselect-item", highlightedIndex === index && "bn-multiselect-item--selected"), "aria-selected": isSelected }),
                        React.createElement(Checkbox, { checked: isSelected, 
                            // Ignore onchange, we will handle onclick through downshift
                            onChange: noop }, itemToString(item))));
                }),
                footerMessage && footerMessage.length > 0 && (React.createElement("div", { className: "bn-multiselect-footer-item" }, footerMessage)),
                itemsToDisplay && items.length > itemsToDisplay && (React.createElement("div", { className: "bn-multiselect-item", style: { textAlign: "center", padding: "10px" } },
                    "Showing results ",
                    itemsToDisplay,
                    " of total ",
                    items.length,
                    ". Please refine your search."))))));
};
export default MultiSelectFieldAsync;
/**
 * We want to use values for keys whenever possible for rerender performance
 * when items change.  However, value could be an object, in which case
 * we need a fallback  value.
 * @param value
 * @param index
 */
function keyIndex(value, index) {
    if (typeof value === "object") {
        return "::index-".concat(index);
    }
    return String(value);
}
function itemToString(item) {
    var _a;
    if (!item) {
        return "";
    }
    return (_a = item.label) !== null && _a !== void 0 ? _a : String(item.value);
}
function getItemsToDisplay(items, itemsToDisplay) {
    if (itemsToDisplay && items.length > itemsToDisplay) {
        return items.slice(0, itemsToDisplay);
    }
    return items;
}
function noop() { }
function consumeMouseEvent(e) {
    e.preventDefault();
}
//# sourceMappingURL=MultiSelectFieldAsync.js.map